import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, SectionTag, SectionTitle, StatusBadge } from "./shared";
import { CORRIDOR_NODES } from "../data/site";

const CENTER = { x: 500, y: 300 };

function curveTo(node) {
  const mx = (CENTER.x + node.x) / 2;
  const my = (CENTER.y + node.y) / 2 - 40;
  return `M ${CENTER.x} ${CENTER.y} Q ${mx} ${my} ${node.x} ${node.y}`;
}

export function CorridorMap() {
  const [active, setActive] = useState(CORRIDOR_NODES[0]);
  const satellites = CORRIDOR_NODES.filter((n) => !n.center);

  return (
    <section id="corridor" className="relative py-28 lg:py-40 bg-[#0A0A0A] overflow-hidden" data-testid="corridor-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionTag>The Yamuna Growth Corridor</SectionTag>
        <div className="grid lg:grid-cols-2 gap-8 items-end mb-14">
          <SectionTitle className="text-4xl sm:text-5xl lg:text-6xl">
            This Is Bigger Than <span className="text-gold-gradient italic">One Township.</span>
          </SectionTitle>
          <Reveal delay={0.15}>
            <p className="text-sm text-[#A1A1AA] leading-relaxed max-w-md lg:ml-auto">
              Gaur Yamuna City sits at the centre of a network of aviation, industry, entertainment,
              logistics and urban infrastructure. Explore each node of the corridor.
            </p>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 items-stretch">
          <Reveal className="lg:col-span-2">
            <div className="relative border border-[#D4AF37]/15 bg-[#0D0D0D] rounded-[2px] overflow-hidden" data-testid="corridor-map">
              <svg viewBox="0 0 960 620" className="w-full h-auto" role="img" aria-label="Yamuna Growth Corridor map">
                <defs>
                  <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="4" result="b" />
                    <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
                  </filter>
                  <radialGradient id="nodeGlow">
                    <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.5" />
                    <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
                  </radialGradient>
                </defs>

                {Array.from({ length: 12 }).map((_, i) => (
                  <line key={`v${i}`} x1={i * 90} y1="0" x2={i * 90} y2="620" stroke="#1c1c1c" strokeWidth="1" />
                ))}
                {Array.from({ length: 8 }).map((_, i) => (
                  <line key={`h${i}`} x1="0" y1={i * 90} x2="960" y2={i * 90} stroke="#1c1c1c" strokeWidth="1" />
                ))}

                {satellites.map((n, i) => (
                  <motion.path
                    key={n.id}
                    d={curveTo(n)}
                    fill="none"
                    stroke={active.id === n.id ? "#F3E5AB" : "#D4AF37"}
                    strokeWidth={active.id === n.id ? 1.6 : 0.9}
                    strokeOpacity={active.id === n.id ? 0.95 : 0.4}
                    filter="url(#glow)"
                    initial={{ pathLength: 0 }}
                    whileInView={{ pathLength: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.6, delay: 0.3 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                  />
                ))}

                <circle cx={CENTER.x} cy={CENTER.y} r="60" fill="url(#nodeGlow)" />
                <motion.circle
                  cx={CENTER.x} cy={CENTER.y} r="9" fill="#D4AF37" filter="url(#glow)"
                  initial={{ r: 9 }} animate={{ r: [9, 12, 9] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                />
                <text x={CENTER.x} y={CENTER.y + 34} textAnchor="middle" fill="#F3E5AB" fontSize="13" letterSpacing="3" style={{ textTransform: "uppercase", fontFamily: "Manrope" }}>
                  GAUR YAMUNA CITY
                </text>

                {satellites.map((n) => (
                  <g
                    key={n.id}
                    data-testid={`corridor-node-${n.id}`}
                    onClick={() => setActive(n)}
                    onMouseEnter={() => setActive(n)}
                    className="cursor-pointer"
                  >
                    <circle cx={n.x} cy={n.y} r="22" fill="transparent" />
                    <motion.circle
                      cx={n.x} cy={n.y} r="5"
                      fill={active.id === n.id ? "#F3E5AB" : "#0A0A0A"}
                      stroke="#D4AF37" strokeWidth="1.4"
                      filter="url(#glow)"
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.5, duration: 0.6 }}
                    />
                    <text x={n.x} y={n.y - 14} textAnchor="middle" fill={active.id === n.id ? "#F3E5AB" : "#A1A1AA"} fontSize="10.5" letterSpacing="1.5" style={{ fontFamily: "Manrope" }}>
                      {n.label.toUpperCase()}
                    </text>
                  </g>
                ))}
              </svg>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="h-full border border-[#D4AF37]/15 bg-[#111111] rounded-[2px] p-7 flex flex-col" data-testid="corridor-node-panel">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#71717A] mb-4">Corridor Node</p>
              <AnimatePresence mode="wait">
                <motion.div
                  key={active.id}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -14 }}
                  transition={{ duration: 0.4 }}
                  className="flex-1"
                >
                  <h3 className="font-serif-lux text-2xl lg:text-3xl text-[#F5F5F0] mb-3">{active.label}</h3>
                  <StatusBadge status={active.status} className="mb-5" />
                  <p className="text-sm text-[#A1A1AA] leading-relaxed">{active.desc}</p>
                </motion.div>
              </AnimatePresence>
              <div className="gold-hairline my-6" />
              <div className="flex flex-wrap gap-2">
                {CORRIDOR_NODES.map((n) => (
                  <button
                    key={n.id}
                    data-testid={`corridor-chip-${n.id}`}
                    onClick={() => setActive(n)}
                    className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] rounded-full border transition-colors ${
                      active.id === n.id ? "border-[#D4AF37] text-[#F3E5AB] bg-[#D4AF37]/10" : "border-[#D4AF37]/15 text-[#71717A] hover:text-[#A1A1AA]"
                    }`}
                  >
                    {n.label}
                  </button>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
